import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { CmsService } from '@spartacus/core';
import { combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';

import { ISubcategory, SdSubcategoriesComponent } from '@seb/web-design-system/subcategories';
import {
  BrandThemesEnum,
  DeviceService, IComponentWrapper,
  ILink,
  ImageRatioEnum,
  IPushCard,
  IPushCardWithLayout,
  PositionEnum, PushSizeEnum, SdPushCardComponent,
  SizesEnum,
  ThemesEnum
} from '@seb/web-design-system/core';
import { CustomCategory, EditorialComponentInterface, PushCardComponentInterface } from '@seb/web-common/core/models';
import { HeadingParagraphMapper, LinkMapper, PushCardMapper } from '@seb/web-common/core/mappers';
import { ComponentService, ImageService, TrackingService, UrlMappingService } from '@seb/web-common/core/services';

@Component({
  selector: 'wc-sub-category',
  templateUrl: './sub-category.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SdSubcategoriesComponent, SdPushCardComponent]
})
export class SubCategoryComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {
  pushSizeEnum = PushSizeEnum;
  titleLevel: number;

  subcategories: ISubcategory[];
  pushCardTextConfig: PositionEnum.center | PositionEnum.left | PositionEnum.right = PositionEnum.center;
  pushCardMediaConfig = PositionEnum.left;
  pushCardImageRatio = ImageRatioEnum.r4_3;
  pushCardThemeConfig: ThemesEnum | BrandThemesEnum = ThemesEnum.promo;
  pushCardMediaAsBackground = false;
  pushCardData: IPushCard;
  push: TemplateRef<SdPushCardComponent>;
  isPushPromo = false;
  promotionPushHasBeenViewed = false;
  promotionName: string;
  isMobile$ = false;

  private mobileSubscription: Subscription;

  @Input()
  linkCardSubcategories: boolean;

  @Input()
  themeConfig: ThemesEnum | BrandThemesEnum;

  @Input()
  subcategoriesInput: CustomCategory[];

  @Output()
  wrapper = new EventEmitter<IComponentWrapper>();

  @ViewChild('pushTemplate')
  pushCardTemplate: TemplateRef<SdPushCardComponent>;

  private readonly destroy$: Subject<void> = new Subject();
  constructor(
    private imageService: ImageService,
    private cdRef: ChangeDetectorRef,
    private cmsService: CmsService,
    private pushCardMapper: PushCardMapper,
    private trackingService: TrackingService,
    private componentService: ComponentService,
    private urlMappingService: UrlMappingService,
    private headingParagraphMapper: HeadingParagraphMapper,
    private deviceService: DeviceService
  ) {}

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!this.promotionPushHasBeenViewed) {
      this.promotionPushHasBeenViewed = this.trackingService.checkingForPromoView(
        { nativeElement: document.getElementById('pushComponent') },
        this.isPushPromo,
        this.promotionName
      );
    }
  }

  ngOnInit(): void {
    this.mobileSubscription = this.deviceService.isMobile.pipe(takeUntil(this.destroy$)).subscribe((isMobile) => {
      this.isMobile$ = isMobile;
      this.cdRef.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    this.cmsService
      .getContentSlot('SubCategories')
      .pipe(
        switchMap((cmp) => {
          const pushCardComp = cmp.components?.find((contentSlot) => contentSlot.typeCode === 'PushCardComponent');
          const pushCardComp$ = pushCardComp
            ? this.cmsService
              .getComponentData(pushCardComp.uid)
              .pipe(
                switchMap((pushCardCmp) =>
                  pushCardCmp['links']
                    ? this.componentService.getLazyComponentData(pushCardCmp, 'links')
                    : of(pushCardCmp)
                )
              )
            : of(null);
          const editorialComp = cmp.components?.find((contentSlot) => contentSlot.typeCode === 'EditorialComponent');
          const editorialComp$ = editorialComp ? this.cmsService.getComponentData(editorialComp.uid) : of(null);
          return combineLatest([pushCardComp$, editorialComp$]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(([pushCardCmpData, editorialCmpData]: [PushCardComponentInterface, EditorialComponentInterface]) => {
        if (editorialCmpData) {
          editorialCmpData = {
            ...editorialCmpData,
            size: SizesEnum.xl,
            headlineLevel: '2'
          };

          this.wrapper.emit({
            heading: this.headingParagraphMapper.toHeadingParagraph(editorialCmpData),
            links: [],
            footNotes: []
          });
        }
        if (pushCardCmpData) {
          this.promotionName = pushCardCmpData.headline;
          this.isPushPromo = pushCardCmpData.isPushPromo ? JSON.parse(pushCardCmpData.isPushPromo) : false;
          const mediaContainer$ = pushCardCmpData.mediaContainer
            ? this.imageService.getMediaFromContainer(pushCardCmpData.mediaContainer)
            : of(undefined);
          mediaContainer$.subscribe((mediaContainer) => {
            const pushCardMWithLayout: IPushCardWithLayout = this.pushCardMapper.toPushCardWithLayout(
              pushCardCmpData,
              mediaContainer ? this.imageService.normalizeMedia(mediaContainer) : undefined,
              this.isMobile$
            );
            this.pushCardMediaAsBackground = pushCardMWithLayout.mediaAsBackground;
            this.pushCardTextConfig = pushCardMWithLayout.textConfig;
            this.pushCardMediaConfig = pushCardMWithLayout.mediaConfig;
            this.pushCardImageRatio = pushCardMWithLayout.imageRatio;
            this.pushCardThemeConfig = pushCardMWithLayout.themesConfig;
            this.pushCardData = pushCardMWithLayout.push;
            this.push = this.pushCardTemplate;
          });
        } else {
          this.push = undefined;
          this.pushCardData = undefined;
        }
        this.cdRef.markForCheck();
      });
  }

  ngOnChanges(): void {
    this.toSubCategories(this.subcategoriesInput).subscribe((subCategories) => {
      this.subcategories = subCategories;
      subCategories.sort((catA, catB) => {
        if (catA.isLarge && !catB.isLarge) {
          return -1;
        } else if (!catA.isLarge && catB.isLarge) {
          return 1;
        } else {
          return 0;
        }
      });
      this.cdRef.markForCheck();
    });
  }

  toSubCategories(categories: CustomCategory[]): Observable<ISubcategory[]> {
    const subCategories$: Observable<ISubcategory[]> = combineLatest(
      categories?.map((subCate) => this.subCategoryToISubcategory(subCate))
    );

    return subCategories$;
  }

  subCategoryToISubcategory(category: CustomCategory): Observable<ISubcategory> {
    const categoryUrl = LinkMapper.decodeUrl(category.url);
    return this.urlMappingService.getUrlMappingByCode(categoryUrl).pipe(
      catchError(() => of(null)),
      map((urlMapping) => {
        let finalCategoryUrl = categoryUrl;
        if (urlMapping) {
          finalCategoryUrl = categoryUrl.replace(urlMapping.code, urlMapping.value);
        }
        return {
          name: category.name,
          link: { url: LinkMapper.decodeUrl(finalCategoryUrl), linkName: category.name },
          image: this.imageService.normalizeMedia(category.image),
          isLarge: category.isSetCategory,
          list: { texts: this.toShortDescription(category.shortDescription), icon: 'icon-check' },
          brand: category.brandCorner ? category.brandCorner : null,
          brandLogo: category.brandLogo?.media ? {
            media: category.brandLogo?.media ? category.brandLogo?.media : null
          } : null
        };
      })
    );
  }

  toShortDescription(shortDescription: string): string[] {
    if (!shortDescription) {
      return [];
    }
    return shortDescription
      .match(/(<li>).+?(<\/li>)/g)
      .map((line) => line.replace('<li>', '').replace('</li>', '').trim());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.mobileSubscription?.unsubscribe();
  }

  onPromoClick(): void {
    this.trackingService.onPromoClick(this.isPushPromo, this.promotionName);
  }

  onSubCateClicked(event: ILink): void {
    this.trackingService.sendEvent('clickMarketingSubCategory', {
      subCategoryName: event.linkName
    });
  }

}
